/**
 * ScrapeReviewPanel — Review staged entities from scrape analysis
 *
 * Displays entities extracted by AI, grouped by type.
 * User can accept/reject each entity, then apply selected ones
 * to final Firestore collections.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack, Box, Typography, Button, Chip, Checkbox,
  Divider, Alert, CircularProgress,
} from '@mui/material';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { ScrapeAnalysisService } from '@modules/firebase-core/services/ScrapeAnalysisService';
import type { StagedEntityData, ScrapeAnalysisData } from '@modules/firebase-core/services/ScrapeAnalysisService';

const ENTITY_TYPE_LABELS: Record<string, string> = {
  description: 'Product Description',
  task: 'Tasks',
  variable: 'Variables',
  review: 'Reviews',
  rating: 'Ratings',
  bigNumber: 'Big Numbers',
  studyCase: 'Study Cases',
};

const ENTITY_TYPE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error'> = {
  description: 'primary',
  task: 'info',
  variable: 'secondary',
  review: 'success',
  rating: 'warning',
  bigNumber: 'error',
  studyCase: 'primary',
};

interface ScrapeReviewPanelProps {
  projectId: string;
  userId: string;
  analysisId: string;
  onApplied?: () => void;
  onClose?: () => void;
}

export function ScrapeReviewPanel({
  projectId,
  userId,
  analysisId,
  onApplied,
  onClose,
}: ScrapeReviewPanelProps) {
  const [entities, setEntities] = useState<StagedEntityData[]>([]);
  const [analysis, setAnalysis] = useState<ScrapeAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = React.useMemo(() => new ScrapeAnalysisService(projectId), [projectId]);

  // Load entities and analysis
  useEffect(() => {
    setIsLoading(true);

    const unsubEntities = service.subscribeToEntities(
      analysisId,
      (data) => {
        setEntities(data);
        setIsLoading(false);
      },
      (err) => {
        setError(err.message);
        setIsLoading(false);
      },
    );

    const unsubAnalysis = service.subscribeToAnalysis(
      analysisId,
      (data) => setAnalysis(data),
    );

    return () => {
      unsubEntities();
      unsubAnalysis();
    };
  }, [service, analysisId]);

  // Toggle entity accepted status
  const toggleAccepted = useCallback(async (entityId: string, currentAccepted: boolean | null) => {
    const newAccepted = currentAccepted === true ? false : true;
    await service.updateEntityAccepted(analysisId, entityId, newAccepted);
  }, [service, analysisId]);

  // Accept all
  const acceptAll = useCallback(async () => {
    const updates = entities.map(e => ({ entityId: e.id, accepted: true }));
    await service.bulkUpdateAccepted(analysisId, updates);
  }, [service, analysisId, entities]);

  // Reject all
  const rejectAll = useCallback(async () => {
    const updates = entities.map(e => ({ entityId: e.id, accepted: false }));
    await service.bulkUpdateAccepted(analysisId, updates);
  }, [service, analysisId, entities]);

  // Apply accepted entities
  const handleApply = useCallback(async () => {
    const acceptedIds = entities.filter(e => e.accepted === true).map(e => e.id);
    if (acceptedIds.length === 0) {
      setError('No entities accepted. Accept at least one entity to apply.');
      return;
    }

    setIsApplying(true);
    setError(null);

    try {
      const client = new AgentApiClient();
      const result = await client.applyStagedEntities(analysisId, {
        projectId,
        userId,
        entityIds: acceptedIds,
      });

      if (result.success) {
        onApplied?.();
      } else {
        setError(result.error || 'Failed to apply entities');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply entities');
    } finally {
      setIsApplying(false);
    }
  }, [entities, analysisId, projectId, userId, onApplied]);

  // Group entities by type
  const groupedEntities = React.useMemo(() => {
    const groups: Record<string, StagedEntityData[]> = {};
    for (const entity of entities) {
      if (!groups[entity.entityType]) groups[entity.entityType] = [];
      groups[entity.entityType].push(entity);
    }
    return groups;
  }, [entities]);

  const acceptedCount = entities.filter(e => e.accepted === true).length;
  const allAccepted = entities.length > 0 && entities.every(e => e.accepted === true);
  const someAccepted = entities.some(e => e.accepted === true);

  const handleToggleAll = useCallback(async () => {
    if (allAccepted) {
      await rejectAll();
    } else {
      await acceptAll();
    }
  }, [allAccepted, acceptAll, rejectAll]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Summary — top */}
      {analysis?.summary && (
        <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
          <Typography variant="body2">
            Scraped {(analysis.summary as any)?.urlsProcessed || 0} URLs,
            extracted {entities.length} entities
            {(analysis.summary as any)?.platformsDetected?.length > 0 && (
              <> | Platforms: {(analysis.summary as any).platformsDetected.join(', ')}</>
            )}
          </Typography>
        </Alert>
      )}

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Toolbar: select-all checkbox + counts + Apply button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={allAccepted}
            indeterminate={someAccepted && !allAccepted}
            onChange={handleToggleAll}
            size="small"
          />
          <Typography variant="subtitle2">
            {acceptedCount}/{entities.length} selected
          </Typography>
          {acceptedCount > 0 && (
            <Chip label={acceptedCount} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onClose && <Button size="small" onClick={onClose}>Cancel</Button>}
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            disabled={isApplying || acceptedCount === 0}
            startIcon={isApplying ? <CircularProgress size={16} /> : undefined}
          >
            {isApplying ? 'Applying...' : `Apply ${acceptedCount} Selected`}
          </Button>
        </Box>
      </Box>

      {/* Entity groups */}
      {Object.entries(groupedEntities).map(([type, items]) => (
        <Stack key={type} spacing={0.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip
              label={ENTITY_TYPE_LABELS[type] || type}
              color={ENTITY_TYPE_COLORS[type] || 'primary'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              ({items.length})
            </Typography>
          </Box>

          {items.map((entity) => (
            <Box
              key={entity.id}
              onClick={() => toggleAccepted(entity.id, entity.accepted)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: entity.accepted === true ? 'success.main'
                  : entity.accepted === false ? 'error.main'
                  : 'divider',
                opacity: entity.accepted === false ? 0.5 : 1,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'all 0.15s ease',
              }}
            >
              <Checkbox
                checked={entity.accepted === true}
                size="small"
                sx={{ p: 0.5 }}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleAccepted(entity.id, entity.accepted)}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <EntityPreview entityType={entity.entityType} data={entity.data} />
              </Box>
            </Box>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

// ── Entity Preview ──────────────────────────────────────────────

/** Check if entity name contains [extended] marker */
function isExtended(data: Record<string, unknown>): boolean {
  const name = (data.name || data.nickname || data.featureName || data.description || '') as string;
  return name.includes('[extended]');
}

/** Render extended note below entity preview */
function ExtendedNote({ data }: { data: Record<string, unknown> }) {
  if (!isExtended(data) || !data._extendedNote) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.25 }}>
        <Chip label="Extended" size="small" color="warning" variant="filled" />
      </Box>
      <Typography variant="caption" color="warning.dark" sx={{ fontStyle: 'italic' }}>
        {data._extendedNote as string}
      </Typography>
    </Box>
  );
}

function EntityPreview({ entityType, data }: { entityType: string; data: Record<string, unknown> }) {
  switch (entityType) {
    case 'description':
      return (
        <Box>
          <Typography variant="subtitle2">{data.name as string}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {data.description as string}
          </Typography>
        </Box>
      );

    case 'task':
      return (
        <Box>
          <Typography variant="subtitle2">{data.name as string}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {data.description as string}
          </Typography>
          <ExtendedNote data={data} />
        </Box>
      );

    case 'variable': {
      const taskIds = data.taskIds as string[] | undefined;
      const isGlobal = !taskIds || taskIds.length === 0;
      return (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">{data.name as string}</Typography>
            <Chip label={data.type as string} size="small" variant="outlined" />
            {Boolean(data.required) && <Chip label="required" size="small" color="warning" variant="outlined" />}
            {isGlobal ? (
              <Chip label="global" size="small" color="info" variant="filled" />
            ) : (
              taskIds.map(tid => (
                <Chip key={tid} label={tid} size="small" color="primary" variant="outlined" />
              ))
            )}
          </Box>
          <ExtendedNote data={data} />
        </Box>
      );
    }

    case 'review':
      return (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="subtitle2">{data.nickname as string}</Typography>
            <Typography variant="body2">{'⭐'.repeat(data.rating as number)}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" noWrap>
            {data.reviewtext as string}
          </Typography>
          <ExtendedNote data={data} />
        </Box>
      );

    case 'rating':
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="subtitle2">{data.rating as string} / 5</Typography>
          <Typography variant="body2" color="text.secondary">
            ({data.ratingCount as string} ratings, {data.platform as string})
          </Typography>
        </Box>
      );

    case 'bigNumber':
      return (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="subtitle2" color="primary">{data.amount as string}</Typography>
            <Typography variant="body2" color="text.secondary">
              {data.description as string}
            </Typography>
          </Box>
          <ExtendedNote data={data} />
        </Box>
      );

    case 'studyCase':
      return (
        <Box>
          <Typography variant="subtitle2">{data.featureName as string}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {data.primaryBenefit as string}
          </Typography>
          <ExtendedNote data={data} />
        </Box>
      );

    default:
      return <Typography variant="body2">{JSON.stringify(data).substring(0, 100)}</Typography>;
  }
}
